import { useTranslation } from 'react-i18next';
import { Icon, Typography, Button } from '@/components';
import { useDevelopmentStatus } from '@/hooks';
import { isItemNull } from '@/features/projects/helpers';
import { customRound, europeanFormatNumber } from '@/helpers';

export type HydrogenBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function HydrogenBox({ normalizedProject, isEditable, onEdit }: HydrogenBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const hydrogenTech = normalizedProject.technologies.find((tech: any) => tech.type === 'hydrogen');
  if (!hydrogenTech) return null;

  const item = hydrogenTech.details.item;
  const status = hydrogenTech.details.developmentStatus.status;
  if (!isEditable && !status && isItemNull(item)) return null;

  const roundedInstalledCapacity = customRound(item.installedCapacity);

  const fields = [
    {
      label: t('Installed Capacity'),
      value: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    },
    {
      label: t('Development Status'),
      value: mapDevelopmentStatus(status) || null,
    },
  ];

  const visibleFields = isEditable
    ? fields
    : fields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="hydrogen" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Hydrogen Details & Design')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>

      {(isEditable || item.information) && (
        <Typography as="p" size="body-2" weight="semibold" relaxed>
          {t('Information')}:{' '}
          <span className="ml-3 text-base font-normal whitespace-pre-wrap text-neutral-900">
            {item.information ?? (isEditable ? '—' : null)}
          </span>
        </Typography>
      )}
    </div>
  );
}
